import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { withTranslation } from "react-i18next";
import { Button, Col } from "reactstrap";
import actionPlansService from "../../../services/ActionPlansService";
import moment from "moment";
import InactiveModal from "./InactiveModal";
import permissionsService from "services/permissionsService";
import { SendaCustomSwal } from "components/Common/SendaCustomSwal";
import DynamicMaterialTable from "components/Common/MaterialTableContainer";
import useTableZoom from "components/Common/useTableZoom";
import Loading from "components/Common/Loading";
import { IconButton, Tooltip } from "@mui/material";
import { Funnel, Plus, Trash, Prohibit } from "@phosphor-icons/react";
import ModalFilter from "./ModalFilter";
import {
	getTableHeaderCellProps,
	getTableBodyCellProps,
} from "../../../components/Common/MaterialTableStyles";
import "./antd-overrides.css"; // CSS específico para sobrescrever padding do Ant Design

const PlanosDeAcao = () => {
	// ========== CONFIGURAÇÃO DE LARGURAS MÍNIMAS E MÁXIMAS DAS COLUNAS ==========
	const COLUMN_WIDTHS = {
		nome: { min: 130 },                    // Largura mínima da coluna "Nome" (sem máximo - cresce livremente)
		realizado: { min: 80, max: 80 },       // Largura mín/máx da coluna "Realizado" (em pixels)
		prazo: { min: 100, max: 100 },         // Largura mín/máx da coluna "Prazo" (em pixels)
		responsavel: { min: 120, max: 120 },   // Largura mín/máx da coluna "Responsável" (em pixels)
		aprovador: { min: 120, max: 120 },     // Largura mín/máx da coluna "Aprovador" (em pixels)
		departamento: { min: 150, max: 150 },  // Largura mín/máx da coluna "Departamento" (em pixels)
		actions: { min: 30, max: 30 },         // Largura mín/máx da coluna "Actions" (em pixels)
	};
	// ================================================================

	const [data, setData] = useState([]);
	const [openModal, setOpenModal] = useState(false);
	const [activeActionPlanModal, setactiveActionPlanModal] = useState(null);
	const [actionPlan, setActionPlan] = useState("");
	const [permissions, setPermissions] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [modalFilterOpen, setModalFilterOpen] = useState(false);

	const navigate = useNavigate();

	document.title = "Senda | Planos de Ação";

	// Usar o hook customizado para zoom e cálculos de largura
	const { zoomLevel, getColumnWidth } = useTableZoom(COLUMN_WIDTHS);

	const columns = useMemo(
		() => [
			{
				header: "Nome",
				accessorKey: "guideline",
				filterFn: "contains",
				widthPixels: getColumnWidth("nome"),
				minWidthPixels: COLUMN_WIDTHS.nome.min,
				// Não tem maxWidthPixels - pode crescer livremente
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("firstColumn"),
				Cell: ({ cell }) => {
					const guidelineName = cell.getValue();
					return (
						<span
							style={{
								cursor: "pointer",
								display: "block", // Permite que o texto quebre corretamente
								maxWidth: "100%", // Respeita o tamanho da célula
								wordBreak: "break-word", // Quebra o texto se for muito grande
								overflowWrap: "break-word", // Compatibilidade extra para quebra de palavras
								padding: "3px",
								lineHeight: "1.2", // Melhora a legibilidade com quebra de linha
							}}
							onMouseEnter={(e) => {
								e.target.style.textDecoration = "underline";
							}}
							onMouseLeave={(e) => {
								e.target.style.textDecoration = "none";
							}}
							onClick={() => editarItem(cell.row.original)}
						>
							{guidelineName}
						</span>
					);
				},
			},
			{
				header: "Realizado",
				accessorKey: "porcentagem_realizada",
				filterFn: "contains",
				enableColumnFilter: false,
				widthPixels: getColumnWidth("realizado"),
				minWidthPixels: COLUMN_WIDTHS.realizado.min,
				maxWidthPixels: COLUMN_WIDTHS.realizado.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
				Cell: ({ cell }) => (
					<div
						style={{
							textAlign: "center",
							wordBreak: "break-word",
							overflowWrap: "break-word",
							lineHeight: "1.2",
						}}
					>
						{cell.getValue()}%
					</div>
				),
			},
			{
				header: "Prazo",
				accessorKey: "deadline",
				// filterFn: "contains",
				widthPixels: getColumnWidth("prazo"),
				minWidthPixels: COLUMN_WIDTHS.prazo.min,
				maxWidthPixels: COLUMN_WIDTHS.prazo.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
				accessorFn: (originalRow) => moment(originalRow.deadline),
				filterVariant: "date",
				Cell: ({ cell, row }) => {
					const deadlineDate = moment(cell.getValue());
					const today = moment();
					const rowData = row.original;
					
					let cellStyle = {
						padding: "3.5px",
						textAlign: "center",
						width: "100%",
					};

					// Verifica se o plano está 100% realizado (todos os objetivos completos)
					const allObjectivesCompleted = rowData.porcentagem_realizada === 100;

					// Só marca em vermelho se o prazo passou E o plano não está 100% realizado
					if (deadlineDate.isBefore(today, "day") && !allObjectivesCompleted) {
						cellStyle = {
							...cellStyle,
							color: "white",
							backgroundColor: "red",
						};
					}

					return (
						<div style={cellStyle}>
							{deadlineDate.format("DD/MM/YYYY")}
						</div>
					);
				},
			},
			{
				header: "Responsável",
				accessorKey: "responsible.lastname",
				filterFn: "contains",
				filterVariant: "select",
				widthPixels: getColumnWidth("responsavel"),
				minWidthPixels: COLUMN_WIDTHS.responsavel.min,
				maxWidthPixels: COLUMN_WIDTHS.responsavel.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
				Cell: ({ cell }) => (
					<div
						style={{
							wordBreak: "break-word",
							overflowWrap: "break-word",
							lineHeight: "1.2",
						}}
					>
						{cell.getValue()}
					</div>
				),
			},
			{
				header: "Aprovador",
				accessorKey: "approving.lastname",
				filterFn: "contains",
				filterVariant: "select",
				widthPixels: getColumnWidth("aprovador"),
				minWidthPixels: COLUMN_WIDTHS.aprovador.min,
				maxWidthPixels: COLUMN_WIDTHS.aprovador.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
				Cell: ({ cell }) => (
					<div
						style={{
							wordBreak: "break-word",
							overflowWrap: "break-word",
							lineHeight: "1.2",
						}}
					>
						{cell.getValue()}
					</div>
				),
			},
			{
				header: "Departamento",
				accessorKey: "organization.name",
				filterFn: "contains",
				filterVariant: "select",
				widthPixels: getColumnWidth("departamento"),
				minWidthPixels: COLUMN_WIDTHS.departamento.min,
				maxWidthPixels: COLUMN_WIDTHS.departamento.max,
				...getTableHeaderCellProps(),
				...getTableBodyCellProps(),
				Cell: ({ cell }) => (
					<div
						style={{
							wordBreak: "break-word",
							overflowWrap: "break-word",
							lineHeight: "1.2",
						}}
					>
						{cell.getValue()}
					</div>
				),
			},
			{
				accessorKey: "actions",
				widthPixels: getColumnWidth("actions"),
				minWidthPixels: COLUMN_WIDTHS.actions.min,
				maxWidthPixels: COLUMN_WIDTHS.actions.max,
				enableColumnFilter: false,
				enableColumnActions: false,
				enableSorting: false,
				enableResizing: false, // Impede redimensionamento
				...getTableHeaderCellProps(),
				...getTableBodyCellProps("actionColumn"),
				Cell: ({ row }) => (
					<div style={{ display: "flex", width: "100%" }}>
						<div>
							<Tooltip title="Excluir" placement="top">
								<IconButton
									style={{
										background: "none",
										border: "none",
										padding: 0,
										cursor: "pointer",
										color: "black",
									}}
									onClick={() => excluirItem(row.original)}
								>
									<Trash size={16} />
								</IconButton>
							</Tooltip>
						</div>
					</div>
				),
				align: "center",
			},
		],
		[zoomLevel], // Adicionado zoomLevel como dependência
	);

	const editarItem = (rowData) => {
		navigate(`form/${rowData.id}`);
	};

	function onCloseModal() {
		setOpenModal(false);
	}
	const excluirItem = (rowData) => {
		setActionPlan(rowData);
		setOpenModal(true);
	};

	function onDelete() {
		setactiveActionPlanModal(true);
	}

	function handleOpenModalFilter() {
		setModalFilterOpen(true);
	}

	function handleCloseModalFilter() {
		setModalFilterOpen(false);
	}

	const inactivate = async () => {
		try {
			if (actionPlan) {
				await actionPlansService.put(actionPlan.id, {
					active: 0,
				});
				await getItems();
				SendaCustomSwal.fire({
					iconHtml: '<i class="success-icon"></i>',
					title: "Sucesso",
					text: "Plano de ação desativado com sucesso!",
					customClass: {
						confirmButton: "custom-confirm-button",
					},
				});
			}
		} catch (error) {
			console.log(error);
			SendaCustomSwal.fire({
				iconHtml: '<i class="error-icon"></i>',
				title: "Oops...",
				text: "Algo deu errado ao desativar o plano de ação!",
				customClass: {
					confirmButton: "custom-confirm-button",
				},
			});
		}
		setOpenModal(false);
		setactiveActionPlanModal(false);
	};

	const getItems = async () => {
		try {
			const response = await actionPlansService.get();
			setData(response);
		} catch (error) {
			console.log(error);
		}
	};

	const getAll = async () => {
		setIsLoading(true);
		await getItems();
		await getPermissions();
		setIsLoading(false);
	};

	useEffect(() => {
		getAll();
	}, []);

	const getPermissions = async () => {
		try {
			const response = await permissionsService.getPermissions();
			setPermissions(response);
		} catch (error) {
			console.log(error);
		}
	};

	const getActionPlansByYear = async (year) => {
		try {
			const response = await actionPlansService.getByYear(year);
			setData(response);
		} catch (error) {
			console.log(error);
		}
	};

	const handleConfirmClick = async (year) => {
		if (year === "todos") {
			getAll();
		} else {
			setIsLoading(true);
			await getActionPlansByYear(year);
			setIsLoading(false);
		}
		handleCloseModalFilter();
	};

	useEffect(() => {
		if (activeActionPlanModal) {
			inactivate();
		}
	}, [activeActionPlanModal]);

	if (!permissions.find((item) => item.role === "actionPlanList")) return;

	return (
		<Loading isLoading={isLoading}>
			<div className="page-content">
				<div
					className="container-fluid"
					style={{
						backgroundColor: "white",
						padding: 20,
						borderRadius: 10,
						maxWidth: "100%",
						overflow: "hidden",
						// marginTop: 25,
					}}
				>
					<Col
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							maxWidth: "100%",
						}}
					>
						<h4
							style={{
								fontSize: "18px",
								fontWeight: "bold",
							}}
						>
							Planos de Ação
						</h4>

						<div style={{ display: "flex", flexShrink: 0 }}>
							<Link to="/planos-de-acao/form">
								<Button
									type="button"
									style={{
										marginBottom: 20,
										background: "none",
										color: "black",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: "2px",
										marginRight: 10,
										whiteSpace: "nowrap",
									}}
								>
									<Plus size={16} /> Adicionar Plano de Ação
								</Button>
							</Link>
							<Link to="/planos-de-acao/inactives">
								<Button
									type="button"
									style={{
										marginBottom: 20,
										background: "none",
										color: "black",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										gap: "2px",
										marginRight: 10,
										whiteSpace: "nowrap",
									}}
								>
									<Prohibit size={16} />  Planos Inativos
								</Button>
							</Link>
							<Button
								style={{
									marginBottom: 20,
									background: "none",
									color: "black",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: "2px",
									whiteSpace: "nowrap",
								}}
								onClick={handleOpenModalFilter}
							>
								<Funnel size={16} /> Filtrar
							</Button>
						</div>
					</Col>
					<div style={{ width: "100%", overflow: "hidden" }}>
						<DynamicMaterialTable columns={columns} data={data} />
					</div>

					<InactiveModal
						show={openModal}
						onCloseClick={onCloseModal}
						onDeleteClick={onDelete}
						nomePerfil={actionPlan?.guideline}
					/>

					<ModalFilter
						show={modalFilterOpen}
						onCloseClick={handleCloseModalFilter}
						onConfirmClick={handleConfirmClick}
					/>
				</div>
			</div>
		</Loading>
	);
};
PlanosDeAcao.propTypes = {
	preGlobalFilteredRows: PropTypes.any,
};

export default withTranslation()(PlanosDeAcao);
